import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BikeDetails } from '@/hooks/useBikeDetails';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { BikeImage } from './bike-details/BikeImage';
import { BikeBasicInfo } from './bike-details/BikeBasicInfo';
import { BikeTechInfo } from './bike-details/BikeTechInfo';
import { BikeLastUpdate } from './bike-details/BikeLastUpdate';
import { BikeMaintenanceWarning } from './bike-details/BikeMaintenanceWarning';
import { BikeIoTStatus } from './bike-details/BikeIoTStatus';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCourierRentals } from '@/hooks/useCourierRentals';
import { useRentalMileage } from '@/hooks/useRentalMileage';

interface BikeDetailsCardProps {
  bikeDetails: BikeDetails | null;
}

const BikeDetailsCard: React.FC<BikeDetailsCardProps> = ({ bikeDetails }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Получаем текущую активную аренду курьера
  const { currentRental } = useCourierRentals();
  
  // Получаем данные о пробеге текущей аренды
  const { mileageData } = useRentalMileage(currentRental?.id || null);
  
  // Добавляем логирование для отладки
  React.useEffect(() => {
    if (bikeDetails) {
      console.log('BikeDetailsCard - данные велосипеда:', {
        id: bikeDetails.id,
        model: bikeDetails.model,
        gps_tag: bikeDetails.gps_tag,
        movement_blocked: bikeDetails.movement_blocked,
        speed_limit: bikeDetails.speed_limit,
        max_allowed_speed: bikeDetails.max_allowed_speed,
        last_service_date: bikeDetails.last_service_date
      });
    }
  }, [bikeDetails]);
  
  const handleViewOnMap = () => {
    navigate('/courier/location-history');
  };
  
  // Формируем пробег для отображения: приоритет пробегу аренды, иначе общий пробег велосипеда
  const displayMileage = React.useMemo(() => {
    if (mileageData?.totalMileage && mileageData.totalMileage > 0) {
      return `${mileageData.totalMileage} км`;
    }
    return bikeDetails?.mileage || 'Не указан';
  }, [mileageData?.totalMileage, bikeDetails?.mileage]);
  
  return (
    <Card className="shadow-md">
      <CardHeader className={`${isMobile ? 'pb-1 pt-3' : 'pb-2'}`}>
        <CardTitle className="text-xl flex justify-between items-center">
          <span>Мой велосипед</span>
          {bikeDetails?.online !== undefined && <BikeIoTStatus online={bikeDetails.online} />}
        </CardTitle>
      </CardHeader>
      <CardContent className={`space-y-${isMobile ? '3' : '4'}`}>
        {bikeDetails ? (
          <>
            <BikeImage model={bikeDetails.model} photoUrl={bikeDetails.photo_url} />
            
            <BikeBasicInfo model={bikeDetails.model} serialNumber={bikeDetails.serial_number} />
            
            <BikeTechInfo 
              batteryPercentage={bikeDetails.battery_percentage}
              averageSpeed={bikeDetails.speed || bikeDetails.current_speed}
              mileage={displayMileage}
              online={bikeDetails.online}
              bikeId={bikeDetails.id}
              movementBlocked={bikeDetails.movement_blocked}
              speedLimit={bikeDetails.speed_limit}
              maxAllowedSpeed={bikeDetails.max_allowed_speed}
            />
            
            <BikeLastUpdate lastServiceDate={bikeDetails.last_service_date} />
            
            {bikeDetails.status === 'maintenance' && <BikeMaintenanceWarning />}
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleViewOnMap}
              size={isMobile ? "sm" : "default"}
            >
              <MapPin className={`mr-2 h-4 w-${isMobile ? '3' : '4'}`} />
              История перемещений
            </Button>
          </>
        ) : (
          <div className="py-8 text-center">
            <p className="text-muted-foreground">У вас нет назначенного велосипеда</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BikeDetailsCard;
